import { useState } from 'react'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import {
    IInventoryAdjustmentCreateState,
    IInventoryAdjustmentRow,
    IInventoryCountEntriesByTakeMap,
    IInventoryCountLine,
    IInventoryCountScheduleRow,
    IInventoryStockTake,
    IInventoryStockTakeCreateState,
    IInventoryStockTakeOverrideMap,
    IInventoryStockTakesTab,
} from 'types/inventoryControl.interface'
import {
    ADJUSTMENT_REASON_KEYS,
    ADJUSTMENT_REASON_META,
    ADJUSTMENT_STATUS_META,
    ADJUSTMENTS_INTRO,
    CHARGE_KIND_KEYS,
    chargeToDisplay,
    countProgressLabel,
    INVENTORY_DEMO_ADJUSTMENTS,
    INVENTORY_DEMO_COUNT_SCHEDULE,
    INVENTORY_DEMO_COUNT_SHEETS,
    INVENTORY_DEMO_STOCK_TAKES,
    lineOverDeviationLimit,
    signedGhs,
    stockTakeVarianceTone,
    STOCK_TAKE_ABC_NOTE,
    STOCK_TAKE_APPROVER_ROLE,
    STOCK_TAKE_DEVIATION_LIMIT_GHS,
    STOCK_TAKE_LOCATION_OPTIONS,
    STOCK_TAKE_SCHEDULE_CAPTION,
    STOCK_TAKE_STATUS_META,
    STOCK_TAKE_SUPERVISOR_ROLE,
} from '../inventoryControlDemoData'
import { INV_DEMO_ITEMS } from '../inventoryDemoData'
import { StockTakeCreateModal } from './StockTakeCreateModal'
import { StockTakeDetail } from './StockTakeDetail'
import { InventoryAdjustmentModal } from './InventoryAdjustmentModal'
import { CellValue, MicroNote, SheetCaption, SheetNoteText, VarianceValue } from './InventoryStockTakes.styled'

const STOCK_TAKES_TABS: IInventoryStockTakesTab[] = [
    { id: 'counts', label: 'Counts' },
    { id: 'adjustments', label: 'Adjustments' },
]

export const InventoryStockTakes = () => {
    const [tab, setTab] = useState<IInventoryStockTakesTab['id']>('counts')
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [createModal, setCreateModal] = useState(false)
    const [adjustmentModal, setAdjustmentModal] = useState(false)
    const [localRows, setLocalRows] = useState<IInventoryStockTake[]>([])
    const [localAdjustments, setLocalAdjustments] = useState<IInventoryAdjustmentRow[]>([])
    const [overrides, setOverrides] = useState<IInventoryStockTakeOverrideMap>({})
    const [entriesByTake, setEntriesByTake] = useState<IInventoryCountEntriesByTakeMap>({})

    const usingDemo = USE_OPS_MOCKS && INVENTORY_DEMO_STOCK_TAKES.length > 0

    const rows: IInventoryStockTake[] = [...localRows, ...INVENTORY_DEMO_STOCK_TAKES].map((row) =>
        overrides[row.id] ? { ...row, ...overrides[row.id] } : row,
    )

    const adjustmentRows: IInventoryAdjustmentRow[] = [...localAdjustments, ...INVENTORY_DEMO_ADJUSTMENTS]

    const entryStats = (row: IInventoryStockTake) => {
        const entryMap = entriesByTake[row.id]
        if (!entryMap) return { count: 0, value: 0 }
        const sheet = INVENTORY_DEMO_COUNT_SHEETS[row.id] ?? []
        let count = 0
        let value = 0
        sheet.forEach((line) => {
            if (line.countedQty == null && entryMap[line.id] != null) {
                count += 1
                value += (entryMap[line.id] - line.systemQty) * line.unitCost
            }
        })
        return { count, value }
    }

    const effectiveVariance = (row: IInventoryStockTake): number | null => {
        const stats = entryStats(row)
        if (row.varianceGhs == null && stats.count === 0) return null
        return (row.varianceGhs ?? 0) + stats.value
    }

    const selected = rows.find((row) => row.id === selectedId) || null

    const handleCreate = (state: IInventoryStockTakeCreateState) => {
        const id = `ST-${String(9 + localRows.length).padStart(2, '0')}`
        const scheduledLabel = format(state.scheduledDate, 'd MMM')
        setLocalRows((prev) => [
            {
                id,
                name: state.name.trim(),
                location: state.location.label,
                scheduledLabel,
                startedLabel: null,
                countedLines: 0,
                totalLines: null,
                varianceGhs: null,
                sheetValueGhs: null,
                status: 'SCHEDULED',
                approvedBy: null,
                approvedAt: null,
                blind: true,
                deviationLimitGhs: STOCK_TAKE_DEVIATION_LIMIT_GHS,
            },
            ...prev,
        ])
        toast.success(
            `Stock take scheduled — ${id} · ${state.location.label} · ${scheduledLabel}${state.cadence ? ` · ${state.cadence.label}` : ''}`,
        )
    }

    const handleCreateAdjustment = (state: IInventoryAdjustmentCreateState) => {
        const id = `ADJ-${121 + localAdjustments.length}`
        const catalogItem = INV_DEMO_ITEMS.find((item) => item.id === state.item.value)
        const valueGhs = catalogItem ? -(state.quantity * catalogItem.unitCostGhs) : 0
        setLocalAdjustments((prev) => [
            {
                id,
                dateLabel: format(new Date(), 'd MMM'),
                reason: ADJUSTMENT_REASON_KEYS[Number(state.reason.value)],
                itemsSummary: `${state.item.label} ×${state.quantity}`,
                valueGhs,
                chargeTo: { kind: CHARGE_KIND_KEYS[Number(state.chargeToKind.value)], label: state.chargeToLabel.trim() },
                status: 'OPEN',
                note: state.note.trim() || null,
            },
            ...prev,
        ])
        toast.success(`Adjustment recorded — ${id} · OPEN, awaiting sign-off`)
    }

    const handleCount = (row: IInventoryStockTake) => (line: IInventoryCountLine, countedQty: number) => {
        setEntriesByTake((prev) => ({
            ...prev,
            [row.id]: { ...(prev[row.id] ?? {}), [line.id]: countedQty },
        }))
        toast.success(
            row.blind === true
                ? `Counted ${line.item} — ${countedQty}`
                : `Counted ${line.item} — ${countedQty} (system ${line.systemQty})`,
        )
    }

    const handleSubmitForApproval = (row: IInventoryStockTake) => {
        setOverrides((prev) => ({ ...prev, [row.id]: { ...prev[row.id], status: 'AWAITING_APPROVAL' } }))
        toast.success(`Count submitted for approval — ${row.id}`)
    }

    const breachedLineCount = (row: IInventoryStockTake): number => {
        const limit = row.deviationLimitGhs
        if (limit == null) return 0
        const entryMap = entriesByTake[row.id] ?? {}
        const sheet = INVENTORY_DEMO_COUNT_SHEETS[row.id] ?? []
        return sheet.filter((line) =>
            lineOverDeviationLimit(
                line.countedQty == null && entryMap[line.id] != null
                    ? { ...line, countedQty: entryMap[line.id] }
                    : line,
                limit,
            ),
        ).length
    }

    const handleApprove = (row: IInventoryStockTake) => {
        setOverrides((prev) => ({
            ...prev,
            [row.id]: {
                ...prev[row.id],
                status: 'COMPLETED',
                approvedBy: breachedLineCount(row) > 0 ? STOCK_TAKE_SUPERVISOR_ROLE : STOCK_TAKE_APPROVER_ROLE,
                approvedAt: format(new Date(), 'd MMM'),
            },
        }))
        toast.success(`Adjustments posted to stock — ${row.id} completed`)
    }

    const columns: IHonestTableColumn<IInventoryStockTake>[] = [
        { id: 'id', header: 'ID', cell: (row) => <CellValue $em>{row.id}</CellValue> },
        { id: 'name', header: 'NAME', minWidth: '16rem', cell: (row) => row.name },
        { id: 'location', header: 'LOCATION', cell: (row) => row.location },
        {
            id: 'scheduled',
            header: 'SCHEDULED / STARTED',
            cell: (row) => (
                <CellValue>{`${row.scheduledLabel}${row.startedLabel ? ` / ${row.startedLabel}` : ''}`}</CellValue>
            ),
        },
        {
            id: 'counted',
            header: 'COUNTED LINES',
            align: 'right',
            cell: (row) => (
                <CellValue>{countProgressLabel(row.countedLines + entryStats(row).count, row.totalLines)}</CellValue>
            ),
        },
        {
            id: 'variance',
            header: 'VARIANCE',
            align: 'right',
            cell: (row) => {
                const variance = effectiveVariance(row)
                if (variance == null) return <OtherText grey>—</OtherText>
                const danger = stockTakeVarianceTone(variance, row.sheetValueGhs) === 'danger'
                return (
                    <VarianceValue $tone={danger ? 'danger' : variance > 0 ? 'positive' : 'normal'}>
                        {signedGhs(variance)}
                    </VarianceValue>
                )
            },
        },
        {
            id: 'status',
            header: 'STATUS',
            cell: (row) => {
                const meta = STOCK_TAKE_STATUS_META[row.status]
                return (
                    <StatusPill status={meta.tone} dot={row.status === 'AWAITING_APPROVAL'}>
                        {meta.label}
                    </StatusPill>
                )
            },
        },
    ]

    const scheduleColumns: IHonestTableColumn<IInventoryCountScheduleRow>[] = [
        { id: 'location', header: 'LOCATION', minWidth: '12rem', cell: (row) => <CellValue $em>{row.location}</CellValue> },
        { id: 'cadence', header: 'CADENCE', minWidth: '10rem', cell: (row) => <CellValue>{row.cadence}</CellValue> },
        { id: 'scope', header: 'SCOPE', cell: (row) => <CellValue>{row.scope}</CellValue> },
        { id: 'lastDone', header: 'LAST DONE', cell: (row) => <CellValue>{row.lastDoneLabel}</CellValue> },
        {
            id: 'nextDue',
            header: 'NEXT DUE',
            minWidth: '10rem',
            cell: (row) =>
                row.state === 'OVERDUE' ? (
                    <StatusPill status='danger' dot>{`Overdue — ${row.nextDueLabel}`}</StatusPill>
                ) : row.state === 'COUNTING' ? (
                    <StatusPill status='info'>Counting now</StatusPill>
                ) : (
                    <CellValue>{row.nextDueLabel}</CellValue>
                ),
        },
        { id: 'owner', header: 'OWNER', cell: (row) => <OtherText grey>{row.owner}</OtherText> },
    ]

    const adjustmentColumns: IHonestTableColumn<IInventoryAdjustmentRow>[] = [
        { id: 'ref', header: 'REF', cell: (row) => <CellValue $em>{row.id}</CellValue> },
        { id: 'date', header: 'DATE', cell: (row) => <CellValue>{row.dateLabel}</CellValue> },
        {
            id: 'reason',
            header: 'REASON',
            cell: (row) => (
                <StatusPill status={ADJUSTMENT_REASON_META[row.reason].tone} dot={row.reason === 'THEFT'}>
                    {ADJUSTMENT_REASON_META[row.reason].label}
                </StatusPill>
            ),
        },
        { id: 'items', header: 'ITEMS', minWidth: '14rem', cell: (row) => row.itemsSummary },
        {
            id: 'value',
            header: 'VALUE',
            align: 'right',
            cell: (row) => (
                <VarianceValue $tone={row.valueGhs < 0 ? 'danger' : row.valueGhs > 0 ? 'positive' : 'normal'}>
                    {signedGhs(row.valueGhs)}
                </VarianceValue>
            ),
        },
        { id: 'chargedTo', header: 'CHARGED TO', minWidth: '12rem', cell: (row) => chargeToDisplay(row.chargeTo) },
        {
            id: 'status',
            header: 'STATUS',
            cell: (row) => {
                const meta = ADJUSTMENT_STATUS_META[row.status]
                return (
                    <StatusPill status={meta.tone} dot={row.status === 'OPEN'}>
                        {meta.label}
                    </StatusPill>
                )
            },
        },
        {
            id: 'note',
            header: 'NOTE',
            minWidth: '16rem',
            cell: (row) => (row.note ? <SheetNoteText>{row.note}</SheetNoteText> : <OtherText grey>—</OtherText>),
        },
    ]

    return (
        <>
            <NavBar />
            <PageWrapper>
                {selected ? (
                    <StockTakeDetail
                        stockTake={selected}
                        lines={INVENTORY_DEMO_COUNT_SHEETS[selected.id] ?? []}
                        entries={entriesByTake[selected.id] ?? {}}
                        onCount={handleCount(selected)}
                        onSubmitForApproval={handleSubmitForApproval}
                        onApprove={handleApprove}
                        onBack={() => setSelectedId(null)}
                    />
                ) : (
                    <>
                        <Flex between center mb='16'>
                            <Flex column gap='0.25rem'>
                                <Flex center gap='0.75rem'>
                                    <MainHeadline>Stock takes</MainHeadline>
                                    {usingDemo && <DemoBadge />}
                                </Flex>
                                <OtherText grey>Cycle counts, variance value and adjustment approval</OtherText>
                            </Flex>
                            {tab === 'counts' ? (
                                <Button variant='primary' onClick={() => setCreateModal(true)}>
                                    New stock take
                                </Button>
                            ) : (
                                USE_OPS_MOCKS && (
                                    <Button variant='primary' onClick={() => setAdjustmentModal(true)}>
                                        New adjustment
                                    </Button>
                                )
                            )}
                        </Flex>
                        <Flex mb='16'>
                            <TabsContainer>
                                {STOCK_TAKES_TABS.map((t) => (
                                    <Tabs key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                                        {t.label}
                                    </Tabs>
                                ))}
                            </TabsContainer>
                        </Flex>
                        {tab === 'counts' ? (
                            <>
                                {usingDemo && INVENTORY_DEMO_COUNT_SCHEDULE.length > 0 && (
                                    <Flex mb='16'>
                                        <Container>
                                            <ContainerHeader>
                                                <Flex between center width='100%'>
                                                    <Flex center gap='0.75rem'>
                                                        <TitleText>Count schedule</TitleText>
                                                        <DemoBadge />
                                                    </Flex>
                                                    <Button size='sm' variant='secondary' onClick={() => setCreateModal(true)}>
                                                        Schedule count
                                                    </Button>
                                                </Flex>
                                            </ContainerHeader>
                                            <ContainerContent>
                                                <HonestTable
                                                    columns={scheduleColumns}
                                                    data={INVENTORY_DEMO_COUNT_SCHEDULE}
                                                    keyField='id'
                                                />
                                                <SheetCaption>{STOCK_TAKE_SCHEDULE_CAPTION}</SheetCaption>
                                                <MicroNote>{STOCK_TAKE_ABC_NOTE}</MicroNote>
                                            </ContainerContent>
                                        </Container>
                                    </Flex>
                                )}
                                {!rows.length ? (
                                    <NoItems title='Stock takes' />
                                ) : (
                                    <HonestTable
                                        columns={columns}
                                        data={rows}
                                        keyField='id'
                                        onClick={(row) => setSelectedId(row.id)}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Flex mb='16'>
                                    <OtherText grey>{ADJUSTMENTS_INTRO}</OtherText>
                                </Flex>
                                {!adjustmentRows.length ? (
                                    <NoItems title='Adjustments' />
                                ) : (
                                    <HonestTable columns={adjustmentColumns} data={adjustmentRows} keyField='id' />
                                )}
                            </>
                        )}
                    </>
                )}
                {createModal && (
                    <StockTakeCreateModal
                        closeModal={() => setCreateModal(false)}
                        locationOptions={STOCK_TAKE_LOCATION_OPTIONS}
                        onCreate={handleCreate}
                    />
                )}
                {adjustmentModal && (
                    <InventoryAdjustmentModal
                        closeModal={() => setAdjustmentModal(false)}
                        onCreate={handleCreateAdjustment}
                    />
                )}
            </PageWrapper>
        </>
    )
}
